import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { Observable, Subject, interval } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { Business } from '../../../../store/business/business.state'
import { selectAllBusinesses } from '../../../../store/business/business.selectors'
import { PaginationComponent } from '../../template/pagination/pagination.component'
import {
  NotificationCampaign,
  NotificationCampaignRecipient,
  NotificationCampaignService
} from '../../../../services/notification-campaign.service'

@Component({
  selector: 'app-notification-campaign-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, CapitalizePipe, PaginationComponent],
  templateUrl: './notification-campaign-detail.component.html',
  styleUrls: ['./notification-campaign-detail.component.scss']
})
export class NotificationCampaignDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private store = inject(Store)
  private destroy$ = new Subject<void>()

  campaignId = ''
  campaign: NotificationCampaign | null = null
  campaignLoading = false

  recipients: NotificationCampaignRecipient[] = []
  recipientsLoading = false
  recipientsTotal = 0
  currentPage = 1
  pageLimit = 20
  pageLastRecord = this.pageLimit

  // filters
  filterBusinessId = ''
  filterPushStatus = ''
  filterEmailStatus = ''

  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)

  readonly deliveryStatusOptions = ['pending', 'sent', 'failed', 'skipped']

  constructor(private campaignService: NotificationCampaignService) {}

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.paramMap.get('id') || ''
    if (!this.campaignId) {
      this.router.navigate(['/superAdmin/notification-campaign'])
      return
    }

    this.loadCampaign()
    this.loadRecipients()

    // ✅ Poll while campaign is actively sending
    interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const active = this.campaign && ['running', 'queued'].includes(this.campaign.status)
          return active ? this.campaignService.getOne(this.campaignId) : []
        })
      )
      .subscribe((res: any) => {
        if (res) {
          this.campaign = res
          this.loadRecipients() // refresh table too while active
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  loadCampaign(): void {
    this.campaignLoading = true
    this.campaignService.getOne(this.campaignId).subscribe({
      next: res => {
        this.campaign = res
        this.campaignLoading = false
      },
      error: () => (this.campaignLoading = false)
    })
  }

  loadRecipients(): void {
    this.recipientsLoading = true
    this.campaignService.getRecipients(this.campaignId, this.buildQuery()).subscribe({
      next: res => {
        this.recipients = res.data
        this.recipientsTotal = res.total
        this.recipientsLoading = false
        this.updatePageLastRecord()
      },
      error: () => (this.recipientsLoading = false)
    })
  }

  onFilterChange(): void {
    this.currentPage = 1
    this.loadRecipients()
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadRecipients()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.recipientsTotal)
  }

  onPageLimitChange(): void {
    this.currentPage = 1
    this.loadRecipients()
  }

  goBack(): void {
    this.router.navigate(['/superAdmin/notification-campaign'])
  }

  retry(): void {
    this.campaignService.retry(this.campaignId).subscribe(() => {
      this.loadCampaign()
      this.loadRecipients()
    })
  }

  stop(): void {
    this.campaignService.stop(this.campaignId).subscribe(() => this.loadCampaign())
  }

  canRetry(): boolean {
    return !!this.campaign && ['failed', 'partial_failed', 'stopped'].includes(this.campaign.status)
  }

  canStop(): boolean {
    return this.campaign?.status === 'running'
  }

  statusClass(status: string): string {
    return `status-badge status-${status}`
  }

  channelLabel(channel: string): string {
    return ({ push: 'Push', email: 'Email', both: 'Push + Email' } as any)[channel] || channel
  }

  progressPercent(): number {
    if (!this.campaign?.totalRecipients) return 0
    return Math.round((this.campaign.processedRecipients / this.campaign.totalRecipients) * 100)
  }

  // ✅ Failure reason — dono channel me se jo bhi fail hua uska error dikhao
  failureReason(r: NotificationCampaignRecipient): string {
    const reasons: string[] = []
    if (r.pushStatus === 'failed' && r.pushError) reasons.push(`Push: ${r.pushError}`)
    if (r.emailStatus === 'failed' && r.emailError) reasons.push(`Email: ${r.emailError}`)
    return reasons.length ? reasons.join(' | ') : '-'
  }

  private buildQuery(): any {
    const query: any = {
      page: this.currentPage,
      limit: this.pageLimit
    }

    if (this.filterBusinessId) {
      query.businessId = this.filterBusinessId
    }

    if (this.filterPushStatus) {
      query.pushStatus = this.filterPushStatus
    }

    if (this.filterEmailStatus) {
      query.emailStatus = this.filterEmailStatus
    }

    return query
  }
}
